import React, { FC } from 'react';

import { Typography, Button, Alert, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import CheckIcon from '@mui/icons-material/Check';
import { formatDateOnly } from '../Formatters';
import { EditableTextField } from '../components/EditableTextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMutation } from '@apollo/client';
import { CANCEL, COMPLETE, SET_MACONOMY_PROJECT_NUMBER, SET_SCHEDULED_END_DATE, UPDATE_COMMENT_TO_ORGANIZATION } from './revisions.graphql';
import moment from 'moment';
import { Revision } from './Details';
import { StyledPaper } from '../theme';
import { useModal } from '../dialogs/useModal';
import { CancelDialog } from './CancelDialog';

export const RevisionDetails: FC<{
  revision: Revision;
  readonly: boolean;
  validate: boolean;
  setValidate: (validate: boolean) => void;
}> = ({ revision, readonly, validate, setValidate }) => {
  const [setMaconomyProjectNumber] = useMutation(SET_MACONOMY_PROJECT_NUMBER);
  const [setScheduledEndDate] = useMutation(SET_SCHEDULED_END_DATE);
  const [updateCommentToOrganization] = useMutation(UPDATE_COMMENT_TO_ORGANIZATION);
  const [cancelRevision, { loading: canceling }] = useMutation(CANCEL, { variables: { input: { id: revision?.id } } });
  const [completeRevision, { loading: completing }] = useMutation(COMPLETE, { variables: { input: { id: revision?.id } } });

  const modal = useModal<undefined, boolean>({ data: undefined });

  const hasProjectLead = (revision?.projectLeads ?? []).length > 0;
  const canShowActions = !revision?.canceledAt && !revision?.completedAt;
  const allTasksCompleted = revision?.checklist?.numberOfTasks === revision?.checklist?.completedTasks;

  const handleCancel = async () => {
    const result = await modal.open();
    if (result) {
      await cancelRevision();
    }
  };

  const handleComplete = async () => {
    if (!hasProjectLead) {
      setValidate(true);
      return;
    }

    await completeRevision();
  };

  return (
    <StyledPaper>
      <Typography variant="h5" component="h3">
        Detaljer
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 35%) 1fr' }, columnGap: 4, rowGap: 2, mt: 2 }}>
        <Typography variant="subtitle1">Prosjektnummer:</Typography>
        {readonly ? (
          <Typography>{revision?.maconomyProjectNumber}</Typography>
        ) : (
          <EditableTextField
            value={revision?.maconomyProjectNumber ?? ''}
            TextFieldProps={{
              variant: 'standard',
            }}
            onUpdate={async (value: string) => {
              await setMaconomyProjectNumber({ variables: { input: { id: revision?.id, projectNumber: value } } });
            }}
          />
        )}

        <Typography variant="subtitle1">Opprettet:</Typography>
        <Typography>{`${formatDateOnly(revision?.createdAt)} av ${revision?.createdByUser?.name}`}</Typography>

        <Typography variant="subtitle1">Fagområde:</Typography>
        <Typography>{revision?.guide?.mainCategory?.title}</Typography>

        {revision?.canceledAt && (
          <>
            <Typography variant="subtitle1">Avbrutt:</Typography>
            <Typography>{`${formatDateOnly(revision.canceledAt)} av ${revision.canceledByUser?.name}`}</Typography>
          </>
        )}

        {revision?.completedAt && (
          <>
            <Typography variant="subtitle1">Ferdigstillt:</Typography>
            <Typography>{`${formatDateOnly(revision.completedAt)}`}</Typography>
          </>
        )}

        <Typography variant="subtitle1">Planlagt ferdig:</Typography>
        {readonly ? (
          <Typography>{formatDateOnly(revision?.scheduledEndDate)}</Typography>
        ) : (
          <DatePicker
            format="DD.MM.YYYY"
            value={revision?.scheduledEndDate ? moment(revision.scheduledEndDate) : null}
            disablePast={false}
            onChange={(date) => setScheduledEndDate({ variables: { input: { id: revision?.id, scheduledEndDate: date } } })}
          />
        )}

        <Typography variant="subtitle1">{readonly ? 'Tekst til forskningsleder:' : 'Tekst til revisjoner.byggforsk.no:'}</Typography>
        {readonly ? (
          <Typography>{revision?.commentToOrganization}</Typography>
        ) : (
          <EditableTextField
            value={revision?.commentToOrganization ?? ''}
            TextFieldProps={{
              multiline: true,
              maxRows: 4,
              variant: 'standard',
            }}
            onUpdate={async (text: string) => {
              await updateCommentToOrganization({ variables: { input: { id: revision?.id, text } } });
            }}
          />
        )}

        {canShowActions && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: 1 }}>
            <Button variant="outlined" color="secondary" onClick={handleCancel} aria-label="Avbryt" disabled={canceling}>
              <DeleteIcon />
              Avbryt revisjon
            </Button>
            <Button variant="contained" color="primary" onClick={handleComplete} aria-label="Ferdig" disabled={!allTasksCompleted || completing}>
              <CheckIcon />
              Ferdig
            </Button>
          </Box>
        )}
        {validate && !hasProjectLead && canShowActions && (
          <Alert variant="standard" severity="error">
            Fyll ut påkrevde roller før revisjonen kan fullføres.
          </Alert>
        )}
      </Box>
      {modal.isOpen && <CancelDialog modal={modal} />}
    </StyledPaper>
  );
};
